import { tagged } from "@mirage/service-logging";
import {
  isSummarySupported,
  StackItemShortcutWithMetadata,
} from "@mirage/service-stacks/service/types";
import { useAtomValue } from "jotai";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { activeStackItemShortcutsAtom } from "../ActiveStack/atoms";

const logger = tagged("stack-summarizable-stack-items");

// Maximum number of stack items to include in a summary
const MAX_ITEMS_TO_INCLUDE = 5;

/**
 * Hook to extract summarizable stack items from the active stack
 *
 * @param {number} maxItems - Maximum number of stack items to include in a summary
 *      If 0, all summarizable stack items will be included
 * @returns {StackItemShortcutWithMetadata[]} Array of StackItemShortcutWithMetadata that can be summarized
 */
export const useSummarizableStackItems = (
  maxItems: number = MAX_ITEMS_TO_INCLUDE,
): StackItemShortcutWithMetadata[] => {
  const items = useAtomValue(activeStackItemShortcutsAtom);
  const [summarizableItems, setSummarizableItems] = useState<
    StackItemShortcutWithMetadata[]
  >([]);

  useEffect(() => {
    try {
      let count = 0;
      const itemsToCheck =
        items?.reduce((acc, item) => {
          if (
            (maxItems === 0 || count < maxItems) &&
            isSummarySupported(item)
          ) {
            if (item.uuid) {
              acc.push(item);
              count++;
            }
          }
          return acc;
        }, [] as StackItemShortcutWithMetadata[]) || [];

      if (!isEqual(itemsToCheck, summarizableItems)) {
        setSummarizableItems(itemsToCheck);
        logger.debug(`Found ${itemsToCheck.length} summarizable stack items`);
      }
    } catch (error) {
      logger.error("Error extracting summarizable stack items", error);
    }
  }, [items, summarizableItems, maxItems]);

  return summarizableItems;
};
